try {
    if (process.env.TOKENS_FEATURE_FLAG === 'true' || process.env.STORYBOOK_TOKENS_FEATURE_FLAG !== 'false') require('./index-new.css');
    else require('./index.css');
  } catch (e) {
    if (e.code !== 'MODULE_NOT_FOUND') {
      throw e; // Re-throw unexpected errors
    }
    console.warn('index.css / index-new.css not found, proceeding without it.');
  }


import { jsx, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames';
import { useRef, useMemo, Fragment, useCallback, forwardRef, useState, useEffect } from 'react';
import { Button } from '@design-system/button';
import { DangerouslySetInnerHtml } from '@phx-husky/dangerously-set-inner-html';

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

const AccordionContent = ({ semanticVariant, index, children, itemsLength, isOpen, }) => {
    const contentItemHeight = useRef([]);
    const ContentTag = semanticVariant === 'dl' ? 'dd' : 'div';
    return (jsx(ContentTag, Object.assign({ className: classNames('cds-accordion__item-content-wrapper', {
            'cds-accordion__item-content-wrapper--last': index === itemsLength - 1,
            'cds-accordion__item-content-wrapper--open': isOpen,
        }), style: {
            height: isOpen ? contentItemHeight.current[index] : 0,
        }, "aria-hidden": !isOpen }, { children: jsx("div", Object.assign({ ref: (child) => {
                if (contentItemHeight.current) {
                    contentItemHeight.current[index] = child === null || child === void 0 ? void 0 : child.offsetHeight;
                }
            }, className: classNames('cds-accordion__item-content', {
                'cds-accordion__item-content--open': isOpen,
            }) }, { children: children })) })));
};

const AccordionTitle = (_a) => {
    var { isOpen, headlineTag = Fragment, openIcon = { icon: 'add' }, closeIcon = { icon: 'close' }, semanticVariant, onToggle, title } = _a, rest = __rest(_a, ["isOpen", "headlineTag", "openIcon", "closeIcon", "semanticVariant", "onToggle", "title"]);
    const TitleTag = useMemo(() => (semanticVariant === 'dl' ? 'dt' : 'div'), [semanticVariant]);
    const HeadlineTag = useMemo(() => (semanticVariant === 'dl' ? Fragment : headlineTag), [headlineTag, semanticVariant]);
    return (jsx(TitleTag, Object.assign({ className: "cds-accordion__opener" }, { children: jsx(HeadlineTag, { children: jsx(Button, Object.assign({ iconPrefix: {
                    props: {
                        icon: !isOpen ? openIcon.icon : closeIcon.icon,
                        classNameContainer: `accordion-item__${isOpen ? 'close' : 'open'}-icon`,
                        isWrapped: true,
                    },
                }, variant: "subtle", size: "large", onClick: onToggle, "aria-expanded": isOpen, className: classNames({
                    active: isOpen,
                }) }, rest, { children: jsx(DangerouslySetInnerHtml, { html: title }) })) }) })));
};

const AccordionItem = (_a) => {
    var { semanticVariant, indexValue, isOpen, itemsLength, headlineTag, openIcon, closeIcon, title, className, handleTogglerAccordionItem, children, disabled } = _a, rest = __rest(_a, ["semanticVariant", "indexValue", "isOpen", "itemsLength", "headlineTag", "openIcon", "closeIcon", "title", "className", "handleTogglerAccordionItem", "children", "disabled"]);
    const Tag = useMemo(() => (semanticVariant === 'div' ? 'section' : 'div'), [semanticVariant]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            handleTogglerAccordionItem();
        }
    }, [handleTogglerAccordionItem]);
    return (jsxs(Tag, Object.assign({ className: classNames('cds-accordion__item', className, {
            active: isOpen,
            'cds-accordion__item--disabled': disabled,
        }), onKeyDown: handleKeyDown }, rest, { children: [jsx(AccordionTitle, { tabIndex: -1, isOpen: isOpen, headlineTag: headlineTag, openIcon: openIcon, closeIcon: closeIcon, semanticVariant: semanticVariant, onToggle: handleTogglerAccordionItem, title: title, disabled: disabled }), jsx(AccordionContent, Object.assign({ semanticVariant: semanticVariant, index: indexValue, itemsLength: itemsLength, isOpen: isOpen }, { children: children }))] })));
};

const AccordionWrapper = ({ items, semanticVariant: Tag, headlineTag, openIcon, closeIcon, selectedIndex, handleTogglerAccordionItem, }) => (jsx(Tag, { children: items.map((_a, index) => {
        var { title, children } = _a, rest = __rest(_a, ["title", "children"]);
        return (jsx(AccordionItem, Object.assign({ title: title, semanticVariant: Tag, indexValue: index, itemsLength: items.length, isOpen: selectedIndex.some((i) => i === index), headlineTag: headlineTag, openIcon: openIcon, closeIcon: closeIcon, handleTogglerAccordionItem: handleTogglerAccordionItem(index), tabIndex: 0 }, rest, { children: children }), title + index));
    }) }));

const noJsCss = `
.cds-accordion__item-content-wrapper {
  height: auto !important;
}

.cds-accordion__item-content {
  opacity: 1 !important;
}

.cds-accordion__opener .icon__prefix {
  display: none !important;
}

.cds-accordion__opener .icon__suffix {
  display: none !important;
}
`;
const Accordion = forwardRef(({ className, semanticVariant, expandedIndex, items, expandVariant = 'multiple', headlineTag, openIcon, closeIcon, viewVariant = 'card', onChange, }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState([]);
    const onCloseAccordionItem = useCallback((index) => {
        if (selectedIndex.some((i) => i === index)) {
            setSelectedIndex([...selectedIndex.filter((el) => el !== index)]);
        }
    }, [selectedIndex]);
    const handleTogglerAccordionItem = useCallback((index) => () => {
        if (onChange)
            onChange(index, !selectedIndex.some((i) => i === index));
        if (expandVariant === 'single') {
            return selectedIndex[0] === index
                ? onCloseAccordionItem(index)
                : setSelectedIndex([index]);
        }
        if (selectedIndex.some((i) => i === index)) {
            return onCloseAccordionItem(index);
        }
        return setSelectedIndex([...selectedIndex, index]);
    }, [expandVariant, selectedIndex, onChange, onCloseAccordionItem]);
    useEffect(() => {
        if (expandedIndex) {
            const disabledItems = items
                .map((el, index) => el.disabled && index)
                .filter(Boolean);
            const filteredExpandedIndex = expandedIndex.filter((el) => !disabledItems.includes(el));
            if (expandVariant === 'single' && filteredExpandedIndex.length) {
                setSelectedIndex([filteredExpandedIndex[0]]);
            }
            if (expandVariant === 'multiple') {
                setSelectedIndex(filteredExpandedIndex);
            }
        }
    }, [expandVariant, expandedIndex, items]);
    return (jsxs("div", Object.assign({ ref: ref, className: classNames('cds-accordion', `cds-accordion__${viewVariant}`, className) }, { children: [jsx(AccordionWrapper, { items: items, semanticVariant: semanticVariant, selectedIndex: selectedIndex, headlineTag: headlineTag, openIcon: openIcon, closeIcon: closeIcon, handleTogglerAccordionItem: handleTogglerAccordionItem, viewVariant: viewVariant }), jsx("noscript", { children: jsx("style", { children: noJsCss }) })] })));
});

export { Accordion };
